/**
 * The geometry repair backstop: turns any shape (pasted JSON, say, or an edit
 * that bypassed the drag bounds) into a valid one. The drag bounds are the
 * primary guard; this catches whatever gets past them.
 */
import { alcoveExtendedBounds } from '../geometry';
import { type LineEdgeRef, lineHosts, resolveLineRef } from '../refs';
import {
  alcoveDepth,
  CORNER_NAMES,
  EDGE_NAMES,
  edgeSegments,
  type FloorPlanCorner,
  type FloorPlanEdgeSegment,
  type FloorPlanRoom,
  roomCeilingHeight,
  segmentOpeningBase,
} from '../schemas';
import { minAlcoveDepth, minAlcoveWidth, minLength, minWidth } from './dragRanges';
import { clamp } from './lengthResolution';

/** Repair any shape into a valid one (backstop; drag bounds are primary). */
export function normalizeShape(shape: FloorPlanRoom): FloorPlanRoom {
  const next = structuredClone(shape);
  next.height = Math.max(0, roomCeilingHeight(next));
  repairHostSegments(next);
  repairRoomCorners(next);
  repairFreeElementShapes(next);
  packAlcoveAndChamferSegments(next);
  clampPrimaryEdgePositionsNonNegative(next);
  growToFitEdgeContent(next);
  separatePrimaryEdgeSegments(next);
  keepFreeElementsInside(next);
  return next;
}

function clampLegsNonNegative(corner: FloorPlanCorner): void {
  corner.dx = Math.max(0, corner.dx);
  corner.dy = Math.max(0, corner.dy);
}

/**
 * Every segment on every host (`lineHosts`): non-negative alcove corner legs,
 * an opening span from the floor up to at most the ceiling (alcoves may carry
 * a raised base or lowered ceiling too), and alcoves at least as big as their
 * chamfers.
 */
function repairHostSegments(room: FloorPlanRoom): void {
  const ceiling = roomCeilingHeight(room);
  for (const { ref, segments } of lineHosts(room)) {
    for (const segment of segments) {
      for (const corner of [segment.backLeftCorner, segment.backRightCorner]) {
        if (corner) clampLegsNonNegative(corner);
      }
      if (segment.openingBase != null) segment.openingBase = Math.max(0, segment.openingBase);
      if (segment.openingHeight != null && ceiling > 0) {
        segment.openingHeight = Math.max(0, Math.min(segment.openingHeight, ceiling - segmentOpeningBase(segment)));
      }
      if (ref.k === 'pe' && segment.edgeSegmentType === 'alcove') {
        segment.depth = Math.max(minAlcoveDepth(segment, ref.edge), alcoveDepth(segment));
        segment.width = Math.max(minAlcoveWidth(segment, ref.edge), segment.width);
      }
    }
  }
}

/** Room corner chamfers: non-negative legs. */
function repairRoomCorners(room: FloorPlanRoom): void {
  for (const cornerName of CORNER_NAMES) {
    const corner = room[cornerName];
    if (corner) clampLegsNonNegative(corner);
  }
}

/** Free elements: non-negative sizes and corner chamfer legs. */
function repairFreeElementShapes(room: FloorPlanRoom): void {
  for (const element of room.freeElements ?? []) {
    element.width = Math.max(0, element.width);
    element.length = Math.max(0, element.length);
    for (const cornerName of CORNER_NAMES) {
      const corner = element[cornerName];
      if (corner) clampLegsNonNegative(corner);
    }
  }
}

interface HostSpan {
  start: number;
  end: number;
}

/** The usable span along a host's line, from `resolveLineRef`; a host with no line (a square corner) has an empty one. */
function hostSpan(room: FloorPlanRoom, ref: LineEdgeRef): HostSpan {
  const line = resolveLineRef(room, ref);
  return { start: line?.minBound ?? 0, end: line?.maxBound ?? 0 };
}

/** Sorts the segments into position order, in place, and returns them. */
const inPositionOrder = (segments: FloorPlanEdgeSegment[]): FloorPlanEdgeSegment[] => segments.sort((first, second) => first.position - second.position);

/**
 * The segments on each alcove wall and chamfer diagonal, in position order,
 * pushed apart so none overlap and kept within the host's span, shrinking any
 * that can't fit. Runs before `separatePrimaryEdgeSegments` sorts the primary
 * edges, which renumbers the alcoves these hosts' refs point at.
 */
function packAlcoveAndChamferSegments(room: FloorPlanRoom): void {
  for (const { ref, segments } of lineHosts(room)) {
    if (ref.k !== 'pe') packIntoSpan(segments, hostSpan(room, ref));
  }
}

/** Move each segment past the one before it and back inside the span, shrinking it if it still doesn't fit. */
function packIntoSpan(segments: FloorPlanEdgeSegment[], { start, end }: HostSpan): void {
  let cursor = start;
  for (const segment of inPositionOrder(segments)) {
    segment.position = Math.max(cursor, Math.min(segment.position, end - segment.width));
    segment.width = Math.min(segment.width, end - segment.position);
    cursor = segment.position + segment.width;
  }
}

/** Primary edge segments start no earlier than their edge does. */
function clampPrimaryEdgePositionsNonNegative(room: FloorPlanRoom): void {
  for (const edge of EDGE_NAMES) {
    for (const segment of edgeSegments(room, edge)) segment.position = Math.max(0, segment.position);
  }
}

/** The room's dimensions grow to fit its edge content. */
function growToFitEdgeContent(room: FloorPlanRoom): void {
  room.width = Math.max(minWidth(room), room.width, 0);
  room.length = Math.max(minLength(room), room.length, 0);
}

/**
 * Each primary edge's segments, in position order, pushed clear of the one
 * before, and any that then runs past the edge's span moved back inside it.
 * The room has grown to fit the segments where they stood and keeps that
 * size, so a segment moved back can overlap its neighbor again.
 */
function separatePrimaryEdgeSegments(room: FloorPlanRoom): void {
  for (const { ref, segments } of lineHosts(room)) {
    if (ref.k === 'pe') separateWithinSpan(segments, hostSpan(room, ref));
  }
}

/** Move each segment past the one before it, then back inside the span's end if it runs past it, never before the span's start. */
function separateWithinSpan(segments: FloorPlanEdgeSegment[], { start, end }: HostSpan): void {
  let cursor = start;
  for (const segment of inPositionOrder(segments)) {
    segment.position = Math.max(segment.position, cursor);
    if (segment.position + segment.width > end) segment.position = Math.max(start, end - segment.width);
    cursor = segment.position + segment.width;
  }
}

/** Free elements shrink to fit inside the room plus its alcoves, and move inside it. */
function keepFreeElementsInside(room: FloorPlanRoom): void {
  const bounds = alcoveExtendedBounds(room);
  for (const element of room.freeElements ?? []) {
    element.width = Math.min(element.width, bounds.maxX - bounds.minX);
    element.length = Math.min(element.length, bounds.maxY - bounds.minY);
    element.x = clamp(element.x, bounds.minX, bounds.maxX - element.width);
    element.y = clamp(element.y, bounds.minY, bounds.maxY - element.length);
  }
}
